import type { TextContent, TextItem } from 'pdfjs-dist/types/src/display/api'
import type { ExtractedBlock, ExtractedLine, ExtractedRun, Rect } from './types'

// ============================================================
// Pure text-layer extraction: pdf.js TextContent → positioned
// lines/blocks in design page coordinates (816×1056). All the
// grouping thresholds live in EXTRACT below so they can be tuned
// against fixtures in one place.
// ============================================================

export const EXTRACT = {
  /** Items whose baseline-y differs less than this × fontSize share a line. */
  lineBaselineTolerance: 0.4,
  /** X-gap larger than this × fontSize inserts a space between runs. */
  wordGapFactor: 0.35,
  /**
   * X-gap larger than this × fontSize splits the baseline into separate
   * lines — same-baseline text in different header columns (agency left,
   * recruiter right) must stay separate blocks, not merge into one wide
   * line.
   */
  columnGapFactor: 3,
  /** Lines whose vertical gap ≤ this × line height group into one block. */
  blockGapFactor: 1.6,
  /** Minimum horizontal bbox overlap ratio for lines to share a block. */
  blockOverlapRatio: 0.3,
  /** |transform[1]|/|transform[2]| above this counts as rotated text. */
  rotationTolerance: 0.02,
  /** Alignment detection: edge jitter below this (design px) is "aligned". */
  alignTolerance: 6,
} as const

export const DESIGN_PAGE = { w: 816, h: 1056 } as const

export interface PageViewport {
  /** pdf.js page.view: [x0, y0, x1, y1] in PDF units. */
  view: readonly number[]
}

interface ParsedItem {
  text: string
  x: number
  /** Top of the glyph box in design coords. */
  y: number
  width: number
  height: number
  fontSizePx: number
  bold: boolean
  italic: boolean
  fontFamily: string
  baselineY: number
}

export interface ExtractResult {
  lines: ExtractedLine[]
  warnings: string[]
}

interface FontStyleInfo {
  fontFamily?: string
}

function parseFontStyle(
  fontName: string,
  styles: Record<string, FontStyleInfo>
): { bold: boolean; italic: boolean; family: string } {
  const styleFamily = styles[fontName]?.fontFamily ?? ''
  const haystack = `${fontName} ${styleFamily}`.toLowerCase()
  const bold = /bold|black|heavy|semibold|demibold/.test(haystack)
  const italic = /italic|oblique/.test(haystack)
  const family = /serif(?!.*sans)|times|georgia|garamond|book/.test(haystack)
    ? 'serif'
    : /mono|courier|consolas/.test(haystack)
      ? 'mono'
      : 'sans'
  return { bold, italic, family }
}

/**
 * Maps an extracted generic family onto the editor font dropdown's CSS
 * stacks (see FONT_OPTIONS in text-toolbar.ts), mirroring the legacy
 * converter's "visually equivalent" bar.
 */
export function mapFontFamily(family: string): string {
  if (family === 'serif') return 'Georgia, serif'
  if (family === 'mono') return "'Courier New', monospace"
  return 'Arial, sans-serif'
}

/**
 * Converts pdf.js text items to positioned lines in design coordinates.
 * PDF y grows upward and transform[4]/[5] give the baseline origin; design
 * y grows downward from the page top.
 */
export function extractLines(
  content: TextContent,
  viewport: PageViewport
): ExtractResult {
  const [x0, y0, x1, y1] = viewport.view
  const scale = DESIGN_PAGE.w / (x1 - x0)
  void y0
  const warnings: string[] = []
  let sawRotated = false

  const items: ParsedItem[] = []
  for (const raw of content.items) {
    const item = raw as TextItem
    if (typeof item.str !== 'string' || item.str.trim().length === 0) continue
    const [a, b, c, d, tx, ty] = item.transform as number[]
    const fontSize = Math.hypot(a, b)
    if (fontSize <= 0) continue
    const skew = Math.max(Math.abs(b), Math.abs(c))
    if (skew > fontSize * EXTRACT.rotationTolerance) {
      sawRotated = true
      continue
    }
    void d
    const fontSizePx = round1(fontSize * scale)
    const baselineY = (y1 - ty) * scale
    const style = parseFontStyle(
      item.fontName,
      (content.styles ?? {}) as Record<string, FontStyleInfo>
    )
    items.push({
      text: item.str,
      x: (tx - x0) * scale,
      // Approximate glyph top from the baseline: ascent ≈ 0.8 × size.
      y: baselineY - fontSizePx * 0.8,
      width: item.width * scale,
      height: fontSizePx,
      fontSizePx,
      bold: style.bold,
      italic: style.italic,
      fontFamily: mapFontFamily(style.family),
      baselineY,
    })
  }
  if (sawRotated) warnings.push('rotated-text-skipped')

  // Group into lines by baseline proximity, then split each baseline at
  // column-sized gaps so multi-column headers keep their structure.
  items.sort((p, q) => p.baselineY - q.baselineY || p.x - q.x)
  const lines: ExtractedLine[] = []
  let bucket: ParsedItem[] = []

  const emitSegment = (segment: ParsedItem[]) => {
    if (segment.length === 0) return
    const runs: ExtractedRun[] = []
    let text = ''
    let prev: ParsedItem | null = null
    for (const it of segment) {
      const gap = prev ? it.x - (prev.x + prev.width) : 0
      const needsSpace =
        prev !== null &&
        gap >
          EXTRACT.wordGapFactor * Math.min(prev.fontSizePx, it.fontSizePx) &&
        !text.endsWith(' ') &&
        !it.text.startsWith(' ')
      const chunk = (needsSpace ? ' ' : '') + it.text
      text += chunk
      const last = runs[runs.length - 1]
      if (last && last.bold === it.bold && last.italic === it.italic) {
        last.text += chunk
      } else {
        runs.push({ text: chunk, bold: it.bold, italic: it.italic })
      }
      prev = it
    }
    const minX = Math.min(...segment.map(i => i.x))
    const maxX = Math.max(...segment.map(i => i.x + i.width))
    const minY = Math.min(...segment.map(i => i.y))
    const maxY = Math.max(...segment.map(i => i.y + i.height))
    const fontSizePx = round1(
      segment.reduce((s, i) => s + i.fontSizePx, 0) / segment.length
    )
    lines.push({
      runs,
      text: text.trim(),
      bbox: rect(minX, minY, maxX - minX, maxY - minY),
      fontSizePx,
      // carried through for block grouping; not part of the public IR
      ...({ _fontFamily: segment[0].fontFamily } as object),
    })
  }

  const flush = () => {
    if (bucket.length === 0) return
    bucket.sort((p, q) => p.x - q.x)
    let segment: ParsedItem[] = []
    for (const it of bucket) {
      const prev = segment[segment.length - 1]
      const gap = prev ? it.x - (prev.x + prev.width) : 0
      if (
        prev &&
        gap > EXTRACT.columnGapFactor * Math.max(prev.fontSizePx, it.fontSizePx)
      ) {
        emitSegment(segment)
        segment = []
      }
      segment.push(it)
    }
    emitSegment(segment)
    bucket = []
  }
  for (const it of items) {
    const ref = bucket[0]
    if (
      ref &&
      Math.abs(it.baselineY - ref.baselineY) >
        EXTRACT.lineBaselineTolerance * Math.max(ref.fontSizePx, it.fontSizePx)
    ) {
      flush()
    }
    bucket.push(it)
  }
  flush()

  return { lines, warnings }
}

function overlapRatio(a: Rect, b: Rect): number {
  const left = Math.max(a.x, b.x)
  const right = Math.min(a.x + a.width, b.x + b.width)
  const overlap = right - left
  if (overlap <= 0) return 0
  return overlap / Math.min(a.width, b.width)
}

/**
 * Groups lines (already filtered to one band) into blocks: consecutive
 * lines that overlap horizontally and sit within a line-height of each
 * other. Alignment is inferred from which edge stays consistent relative
 * to the containing band.
 */
export function groupBlocks(
  lines: ExtractedLine[],
  band: Rect
): ExtractedBlock[] {
  const sorted = [...lines].sort(
    (a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x
  )
  const groups: ExtractedLine[][] = []
  for (const line of sorted) {
    const group = groups.find(g => {
      const last = g[g.length - 1]
      const gap = line.bbox.y - (last.bbox.y + last.bbox.height)
      return (
        gap <= EXTRACT.blockGapFactor * last.bbox.height &&
        gap > -last.bbox.height * 0.5 &&
        overlapRatio(last.bbox, line.bbox) >= EXTRACT.blockOverlapRatio
      )
    })
    if (group) group.push(line)
    else groups.push([line])
  }

  return groups.map(group => {
    const minX = Math.min(...group.map(l => l.bbox.x))
    const maxX = Math.max(...group.map(l => l.bbox.x + l.bbox.width))
    const minY = Math.min(...group.map(l => l.bbox.y))
    const maxY = Math.max(...group.map(l => l.bbox.y + l.bbox.height))
    const bbox = rect(minX, minY, maxX - minX, maxY - minY)
    // Dominant font size = the size covering the most characters.
    const sizeWeights = new Map<number, number>()
    for (const l of group) {
      sizeWeights.set(
        l.fontSizePx,
        (sizeWeights.get(l.fontSizePx) ?? 0) + l.text.length
      )
    }
    const fontSizePx = [...sizeWeights.entries()].sort(
      (a, b) => b[1] - a[1]
    )[0][0]

    const fontFamily =
      (group[0] as unknown as { _fontFamily?: string })._fontFamily ??
      'Arial, sans-serif'

    return {
      lines: group,
      bbox,
      align: inferAlign(group, bbox, band),
      fontSizePx,
      fontFamily,
      colorHex: '#0F172A',
      included: true,
    }
  })
}

function inferAlign(
  lines: ExtractedLine[],
  bbox: Rect,
  band: Rect
): 'left' | 'center' | 'right' {
  if (lines.length === 1) {
    // Single line: judge by where it sits within the band.
    const leftGap = bbox.x - band.x
    const rightGap = band.x + band.width - (bbox.x + bbox.width)
    if (Math.abs(leftGap - rightGap) <= band.width * 0.06) return 'center'
    return rightGap < leftGap ? 'right' : 'left'
  }
  const lefts = lines.map(l => l.bbox.x)
  const rights = lines.map(l => l.bbox.x + l.bbox.width)
  const centers = lines.map(l => l.bbox.x + l.bbox.width / 2)
  const spread = (xs: number[]) => Math.max(...xs) - Math.min(...xs)
  const spreads: Array<['left' | 'right' | 'center', number]> = [
    ['left', spread(lefts)],
    ['right', spread(rights)],
    ['center', spread(centers)],
  ]
  spreads.sort((a, b) => a[1] - b[1])
  const [winner, jitter] = spreads[0]
  if (jitter > EXTRACT.alignTolerance) return 'left'
  return winner
}

/** Lines fully or mostly inside the given band rect. */
export function linesInBand(
  lines: ExtractedLine[],
  band: Rect
): ExtractedLine[] {
  return lines.filter(l => {
    const centerY = l.bbox.y + l.bbox.height / 2
    return centerY >= band.y && centerY <= band.y + band.height
  })
}

function rect(x: number, y: number, width: number, height: number): Rect {
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(width),
    height: Math.round(height),
  }
}

function round1(n: number): number {
  return Math.round(n * 2) / 2
}
